import get from "lodash/get";
import {isFeatureEnabled} from "shared/Utils/FeatureUtils";
import BlogFilled from "shared/Experimental/Icons/BlogFilled";
import InboxFilled from "shared/Experimental/Icons/InboxFilled";
import SettingsFilled from "shared/Experimental/Icons/SettingsFilled";
import {Box} from "@unibuddy/patron";
import BlogLine from "shared/Experimental/Icons/BlogLine";
import InboxLine from "shared/Experimental/Icons/InboxLine";
import SettingsLine from "shared/Experimental/Icons/SettingsLine";
import RecordAnalytics from "shared/Analytics/RecordAnalytics";
import Auth from "shared/Auth/auth";
import withAuth from "shared/Auth/withAuth/withAuth";
import InboxQuery from "shared/Inbox/queries/InboxQuery";
import {withUniversityTheme} from "shared/Styling/hocs/withTheme";
import PropTypes from "prop-types";
import React from "react";
import compose from "lodash/flowRight";
import {graphql} from "react-apollo";
import {connect} from "react-redux";
import {Route} from "react-router-dom";
import {useTranslation} from "@unibuddy/intl";
import focusHeader from "shared/Experimental/A11y/components/SkipNav/FocusHeader";
import {useBooleanFeatureFlag} from "ub-feature-flagging-react";
import {
    CONTENT_RELEASE3_FLAG,
    DISABLE_DOUBLE_NAV_BAR,
    NEXT_BEST_ACTIONS_FLAG,
} from "shared/constants";
import Content from "shared/Icons/Content";
import checkAllowDelinkedForApplicant from "shared/Chat/utils/checkAllowDelinkedForApplicant";
import ExitTab from "../../components/ExitTab/ExitTab";
import s from "./HeaderTabs.pcss";
import IconTab from "../../components/IconTab/IconTab";
import SvgTab from "../../components/SvgTab/SvgTab";
import BuddiesHeaderTab from "../../components/HeaderTabs/BuddiesHeaderTab";
import BuddiesGroupHeaderTab from "../../components/HeaderTabs/BuddiesGroupHeaderTab";
import BuddiesGroupsSubNavHeaderTab from "../../components/HeaderTabs/BuddiesGroupsSubNavHeaderTab";
import NextBestActions from "../../../NextBestActions/containers/NextBestActions";
import {NEXT_BEST_ACTIONS_FLAG_DEFAULT} from "../../../NextBestActions/NextBestActions";

export const getUrls = (props) => {
    const {universitySlug, colourCode, degreeId} = get(props, "match.params", {});
    // root url to be used for routing in different widgets
    let rootUrl = `/embed/${universitySlug}/colour/${colourCode}`;
    if (props.primaryApp === "university-geodude")
        rootUrl = `/embed/${universitySlug}/course/${degreeId}`;
    else if (props.primaryApp === "charmander") rootUrl = `/pwa/${universitySlug}`;

    // we want to retain the url for buddies tab because
    // we want the filters to be hidden in course buddy platform
    let buddiesTabUrl = rootUrl;
    if (degreeId && props.primaryApp === "university-jigglypuff")
        buddiesTabUrl = `/embed/${universitySlug}/course/${degreeId}`;

    return {rootUrl, buddiesTabUrl};
};

export const HeaderTabsComponent = (props) => {
    const disableDoubleNav = useBooleanFeatureFlag(DISABLE_DOUBLE_NAV_BAR, false);
    const showNextBestActions = useBooleanFeatureFlag(
        NEXT_BEST_ACTIONS_FLAG,
        NEXT_BEST_ACTIONS_FLAG_DEFAULT,
    );
    const isLoggedIn = get(props, "authState.token", false);
    const contentRelease3enabled = useBooleanFeatureFlag(CONTENT_RELEASE3_FLAG, false);
    const hasStaffProfilesEnabled = isFeatureEnabled("staffProfiles", props.university);
    const isBlogsEnabled = isFeatureEnabled("userGeneratedContent", props.university);

    const {rootUrl, buddiesTabUrl} = getUrls(props);
    const [buddiesUrl] = React.useState(buddiesTabUrl);
    const {t} = useTranslation("common");
    const pathname = get(props, "location.pathname", "");

    const onSelectTab = (name) => {
        RecordAnalytics.event(
            props,
            `Clicked ${name} Header Bar`,
            {uniSlug: props.universitySlug},
            false,
        );
        focusHeader("content");
    };

    /**
     * Count the total number of unread message in all chat groups
     */
    const getTotalUnreadMessageCount = () =>
        get(props, "chatGroups", []).reduce(
            (total, chatGroup) => total + get(chatGroup, "unreadMessageCount", 0),
            0,
        );

    const closeFullScreen = () => {
        RecordAnalytics.event(
            props,
            "Clicked Exit in Header Bar",
            {uniSlug: props.universitySlug},
            false,
        );
        props.closeFullScreen();
    };

    const numberOfActiveKeys = (obj) => Object.values(obj).filter((value) => value).length;
    const isSmallViewPort = window.matchMedia("(max-width: 480px)");
    const activeTabs = {
        staff: hasStaffProfilesEnabled && get(props, "publicStaffCount", 1) > 0, // TO DO: use the new user module to get this count
        alumni: false, // Temporary placeholder
        inbox: true,
        blogs: true,
        settings: true,
    };
    activeTabs.students = activeTabs.staff || activeTabs.alumni;
    const numberOfActiveTabs = numberOfActiveKeys(activeTabs);
    const showSubNav = disableDoubleNav ? false : isSmallViewPort.matches && numberOfActiveTabs > 4;
    const navBarClass =
        props.primaryApp === "university-jigglypuff" && pathname.includes("/blog/post/")
            ? s.fixedContainer
            : s.container;

    return (
        <nav className={navBarClass}>
            <div className={s.tabContainer}>
                {showSubNav ? (
                    <BuddiesHeaderTab to={`${buddiesUrl}/buddies`} />
                ) : (
                    <BuddiesGroupHeaderTab
                        rootUrl={buddiesUrl}
                        onSelectTab={onSelectTab}
                        activeTabs={activeTabs}
                    />
                )}

                {isBlogsEnabled &&
                    (contentRelease3enabled ? (
                        <IconTab
                            id="blog"
                            to={`${rootUrl}/blog`}
                            name={t("content", "Content")}
                            onClick={() => onSelectTab("Blog")}
                            icon={() => <Content />}
                        />
                    ) : (
                        <IconTab
                            id="blog"
                            to={`${rootUrl}/blog`}
                            name={t("blog", "Blog")}
                            onClick={() => onSelectTab("Blog")}
                            icon={({isActive}) => (isActive ? <BlogFilled /> : <BlogLine />)}
                        />
                    ))}

                <IconTab
                    id="inbox"
                    data-test-id="inbox"
                    name={t("inbox", "Inbox")}
                    onClick={() => onSelectTab("Inbox")}
                    to={`${rootUrl}/inbox`}
                    icon={(isActive) => (isActive ? <InboxFilled /> : <InboxLine />)}
                >
                    {props.unreadMessageCount > 0 && (
                        <span className={s.unreadDot} data-test-id="inbox-count">
                            {getTotalUnreadMessageCount()}
                        </span>
                    )}
                </IconTab>

                {!Auth.loggedIn(props.authState) ? (
                    <IconTab
                        id="login"
                        name={t("signup", "Sign Up")}
                        onClick={() => onSelectTab("Sign Up")}
                        to={`${rootUrl}/auth/register`}
                        icon="icon-Login"
                    />
                ) : (
                    <SvgTab
                        id="settings"
                        name={t("settings", "Settings")}
                        onClick={() => onSelectTab("Settings")}
                        to={`${rootUrl}/settings`}
                        icon={(isActive) => (isActive ? <SettingsFilled /> : <SettingsLine />)}
                    />
                )}

                {get(props, "appState.isFullScreen", false) ? (
                    <ExitTab onClick={closeFullScreen} />
                ) : null}
            </div>
            {showNextBestActions && isLoggedIn && (
                <Box
                    data-test-id="nba-recommendations"
                    display="flex"
                    paddingX="medium"
                    paddingTop="xsmall"
                >
                    <NextBestActions />
                </Box>
            )}
            <Route path={`${buddiesUrl}/buddies`}>
                {({match}) => {
                    const isActive = !!match;
                    if (!isActive || !showSubNav) {
                        return null;
                    }
                    return (
                        <Box display="flex" id="buddiesSubNav" data-test-id="buddies-sub-nav">
                            <BuddiesGroupsSubNavHeaderTab
                                activeClassName={s.subNavItemActive}
                                inactiveClassName={s.subNavItemInactive}
                                rootUrl={buddiesUrl}
                                onSelectTab={onSelectTab}
                                activeTabs={activeTabs}
                            />
                        </Box>
                    );
                }}
            </Route>
        </nav>
    );
};

HeaderTabsComponent.propTypes = {
    universitySlug: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
    appState: state.appState,
});

const inboxQueryOptions = {
    name: "inbox",
    skip: (props) => !Auth.loggedIn(props.authState, "applicant"),
    options(props) {
        const universitySlug = get(props, "match.params.universitySlug", "");
        const meAuthState = get(props, "authState.me");
        const allowDelinked = checkAllowDelinkedForApplicant(universitySlug, meAuthState);

        return {
            variables: {
                universitySlug,
                allowDelinked,
            },
            fetchPolicy: "cache-and-network",
            pollInterval: 10000,
        };
    },
    props({inbox: {loading, applicantInbox}}) {
        return {
            chatGroups: get(applicantInbox, "chatGroups"),
            loading,
        };
    },
};

export default compose(
    connect(mapStateToProps),
    graphql(InboxQuery, inboxQueryOptions),
    withAuth,
    withUniversityTheme(),
)(HeaderTabsComponent);
